<template>
  <div class="invoice-mobile-success" dir="rtl" :style="pageStyle">
    <header class="invoice-mobile-success__header">
      <button
        type="button"
        class="invoice-mobile-success__icon-btn"
        :aria-label="$t('back')"
        @click="goList"
      >
        <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
      </button>
      <h1 class="invoice-mobile-success__title">
        {{ $t("invoice-mobile-success-title") }}
      </h1>
      <span class="invoice-mobile-success__header-spacer" />
    </header>

    <div v-if="loading" class="invoice-mobile-success__loading">
      <v-progress-circular indeterminate color="#5A6B85" size="36" width="3" />
    </div>

    <div v-else class="invoice-mobile-success__body">
      <div class="invoice-mobile-success__hero">
        <div class="invoice-mobile-success__check">
          <v-icon size="36" color="#2E7D32">mdi-check-circle</v-icon>
        </div>
        <p class="invoice-mobile-success__hero-text">
          {{ $t("invoice-mobile-success-hero") }}
        </p>
      </div>

      <div class="invoice-mobile-success__card">
        <div
          v-for="(row, index) in summaryRows"
          :key="'success-row-' + index"
          class="invoice-mobile-success__row"
          :class="{ 'is-emphasis': row.emphasis }"
        >
          <span>{{ row.label }}</span>
          <strong>{{ row.value }}</strong>
        </div>
      </div>

      <div v-if="invoiceShareLink" class="invoice-mobile-success__share">
        <div class="invoice-mobile-success__share-title">
          {{ $t("invoice-mobile-share-invoice-title") }}
        </div>

        <div class="invoice-mobile-success__share-hub">
          <div class="invoice-mobile-success__qr-panel">
            <img
              v-if="qrCodeUrl"
              :src="qrCodeUrl"
              alt="QR"
              class="invoice-mobile-success__qr"
              width="108"
              height="108"
            />
            <span class="invoice-mobile-success__qr-caption">
              {{ $t("invoice-mobile-scan-qr-caption") }}
            </span>
          </div>

          <div class="invoice-mobile-success__share-side">
            <p class="invoice-mobile-success__share-hint">
              {{ $t("invoice-mobile-share-hint") }}
            </p>
            <button
              type="button"
              class="invoice-mobile-success__copy-chip"
              @click="copyInvoiceLink"
            >
              <v-icon size="18" color="#F57C00">mdi-link-variant</v-icon>
              <span class="invoice-mobile-success__copy-chip-text">
                {{ $t("copyInvoiceLink") }}
              </span>
              <v-icon size="16" color="#90A4AE">mdi-content-copy</v-icon>
            </button>
            <div class="invoice-mobile-success__share-twins">
              <button
                type="button"
                class="invoice-mobile-success__twin"
                :disabled="smsLoading || !invoiceId"
                @click="sendInvoiceSms"
              >
                <v-progress-circular
                  v-if="smsLoading"
                  indeterminate
                  size="16"
                  width="2"
                  color="#F57C00"
                />
                <v-icon v-else size="18" color="#F57C00">
                  mdi-message-reply-text-outline
                </v-icon>
                <span>{{ $t("sms") }}</span>
              </button>
              <button
                type="button"
                class="invoice-mobile-success__twin invoice-mobile-success__twin--accent"
                @click="shareInvoiceLink"
              >
                <v-icon size="18" color="#fff">mdi-share-variant-outline</v-icon>
                <span>{{ $t("invoice-mobile-share-button") }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="invoice-mobile-success__actions">
        <button
          type="button"
          class="invoice-mobile-success__btn invoice-mobile-success__btn--primary"
          :disabled="!invoiceUid || pdfLoading"
          @click="downloadPdf"
        >
          <v-progress-circular
            v-if="pdfLoading"
            indeterminate
            size="18"
            width="2"
            color="#fff"
            class="ml-2"
          />
          <v-icon v-else size="20" color="#fff" class="ml-2">mdi-file-pdf-box</v-icon>
          <span>{{ $t("getFilepdf") }}</span>
        </button>

        <div class="invoice-mobile-success__action-row">
          <button
            type="button"
            class="invoice-mobile-success__btn invoice-mobile-success__btn--soft"
            @click="openPrintPreview"
          >
            <v-icon size="18" color="#5A6B85" class="ml-1">mdi-eye-outline</v-icon>
            <span>{{ $t("invoice-mobile-preview") }}</span>
          </button>
          <button
            type="button"
            class="invoice-mobile-success__btn invoice-mobile-success__btn--outline"
            @click="goList"
          >
            <v-icon size="18" color="#546E7A" class="ml-1">mdi-format-list-bulleted</v-icon>
            <span>{{ $t("list") }}</span>
          </button>
        </div>

        <button
          v-if="canCreateNew"
          type="button"
          class="invoice-mobile-success__link"
          @click="goNew"
        >
          {{ $t("invoice-mobile-register-new-invoice") }}
        </button>
      </div>
    </div>

    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      top
      timeout="3500"
    >
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>

<script>
import General from "@/Mixins/general";

export default {
  name: "InvoiceMobileSuccess",
  mixins: [General],
  props: {
    id: { type: [String, Number], default: null },
  },
  data() {
    return {
      loading: true,
      pdfLoading: false,
      smsLoading: false,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "pink",
      invoiceUid: null,
      invoiceId: null,
      invoiceType: null,
      number: null,
      date: null,
      customerName: null,
      total: null,
      currencyName: "",
      itemCount: 0,
    };
  },
  computed: {
    routeId() {
      return this.id || (this.$route.params && this.$route.params.id) || null;
    },
    // Extend white sheet under bottom nav (same approach as InvoiceIndex).
    pageStyle() {
      const appBar = 52;
      const bottomNav =
        this.$store.state.bottomNavigate &&
        this.$vuetify &&
        this.$vuetify.breakpoint &&
        this.$vuetify.breakpoint.mobile
          ? 46
          : 0;
      return {
        "--invoice-success-chrome": `${appBar}px`,
        marginBottom: bottomNav ? `-${bottomNav}px` : "0",
        paddingBottom: bottomNav ? `${bottomNav}px` : "0",
        boxSizing: "border-box",
        background: "#fff",
      };
    },
    invoiceTypeLabel() {
      return this.invoiceType ? this.$t(this.invoiceType) : this.$t("invoice");
    },
    canCreateNew() {
      return (
        this.invoiceType &&
        this.$store.state.permissions &&
        this.$store.state.permissions.includes(this.invoiceType)
      );
    },
    invoiceShareLink() {
      if (!this.invoiceUid) return null;
      const cuid = this.$store.state.cuid
        ? `?cuid=${this.$store.state.cuid}`
        : "";
      return `https://abr.abriron.com/print/invoice/${this.invoiceUid}${cuid}`;
    },
    qrCodeUrl() {
      if (!this.invoiceShareLink) return null;
      return `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(
        this.invoiceShareLink
      )}&size=240x240`;
    },
    summaryRows() {
      const money = (value) => {
        const formatted = this.$numberFormat
          ? this.$numberFormat(value || 0)
          : String(value || 0);
        return this.currencyName
          ? `${formatted} ${this.currencyName}`
          : formatted;
      };
      return [
        {
          label: this.$t("number"),
          value: this.number != null ? String(this.number) : "—",
        },
        {
          label: this.$t("date"),
          value: this.date || "—",
        },
        {
          label: this.$t("customer"),
          value: this.customerName || this.$t("haveNot"),
        },
        {
          label: this.$t("productCount"),
          value: String(this.itemCount || 0),
        },
        {
          label: this.$t("total"),
          value: money(this.total),
          emphasis: true,
        },
      ];
    },
  },
  watch: {
    routeId: {
      immediate: true,
      handler(val) {
        if (val) this.fetchInvoice(val);
      },
    },
  },
  methods: {
    notify(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color || "green";
      this.snackbar = true;
    },
    fetchInvoice(id) {
      this.loading = true;
      this.$axios
        .get(this.$endpoint + "invoice/" + id + "/edit", {
          params: { print: "active" },
        })
        .then((res) => {
          const data = res.data || {};
          this.invoiceUid = data.uid || id;
          this.invoiceId = data.id || null;
          this.invoiceType = data.type || this.$route.query.type || "sell";
          this.number = data.number;
          this.date = data.date;
          this.customerName =
            (data.customer && data.customer.name) ||
            (data.account && data.account.name) ||
            null;
          this.currencyName =
            (data.currency && (data.currency.symbol || data.currency.name)) ||
            this.$store.state.currencySymbol ||
            "";
          const articles = Array.isArray(data.article) ? data.article : [];
          this.itemCount = articles.length;
          if (data.sum != null) {
            this.total = Number(data.sum);
          } else if (data.total != null) {
            this.total = Number(data.total);
          } else {
            let sum = 0;
            articles.forEach((item) => {
              const fee = Number(item.fee) || 0;
              const count = Number(item.count) || 0;
              const discount = Number(item.discount) || 0;
              const tax = Number(item.tax) || 0;
              sum += fee * count - discount + tax;
            });
            this.total = sum - (Number(data.discount) || 0);
          }
        })
        .catch((err) => {
          if (typeof this.serverErrorHandeler === "function") {
            this.serverErrorHandeler(err);
          }
          this.notify(this.$t("error"), "pink");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async copyInvoiceLink() {
      if (!this.invoiceShareLink) return;
      try {
        await navigator.clipboard.writeText(this.invoiceShareLink);
        this.notify(this.$t("copied"), "green");
      } catch (e) {
        this.notify(this.invoiceShareLink, "blue");
      }
    },
    async shareInvoiceLink() {
      if (!this.invoiceShareLink) return;
      try {
        if (navigator.share) {
          await navigator.share({
            title: this.$t("invoice"),
            url: this.invoiceShareLink,
          });
          return;
        }
      } catch (err) {
        if (err && err.name === "AbortError") return;
      }
      return this.copyInvoiceLink();
    },
    sendInvoiceSms() {
      if (!this.invoiceId || this.smsLoading) return;
      this.smsLoading = true;
      this.$axios
        .get(this.$endpoint + "invoice", {
          params: {
            invoiceSMS: "active",
            id: this.invoiceId,
          },
        })
        .then(() => {
          this.notify(this.$t("sent"), "green");
        })
        .catch((err) => {
          if (typeof this.serverErrorHandeler === "function") {
            this.serverErrorHandeler(err);
          }
        })
        .finally(() => {
          this.smsLoading = false;
        });
    },
    openPrintPreview() {
      if (!this.invoiceUid) return;
      this.$router.replace({
        name: "invoicePrint",
        params: { id: this.invoiceUid },
      });
    },
    downloadPdf() {
      if (!this.invoiceUid) return;
      this.pdfLoading = true;
      this.$router.replace({
        name: "invoicePrint",
        params: { id: this.invoiceUid },
        query: { autoPdf: "1" },
      });
    },
    goList() {
      const type = this.invoiceType || "sell";
      if (typeof this.goTo === "function") {
        this.goTo(type + "List");
        return;
      }
      this.$router.push({ name: type + "List" });
    },
    goNew() {
      const type = this.invoiceType || "sell";
      if (typeof this.goTo === "function") {
        this.goTo(type);
        return;
      }
      this.$router.push({ name: type });
    },
  },
};
</script>

<style scoped>
.invoice-mobile-success {
  /* Cascade vh → dvh → svh so Xiaomi keeps header/actions inside chrome */
  height: calc(100vh - var(--invoice-success-chrome, 112px));
  height: calc(100dvh - var(--invoice-success-chrome, 112px));
  height: calc(100svh - var(--invoice-success-chrome, 112px));
  max-height: calc(100vh - var(--invoice-success-chrome, 112px));
  max-height: calc(100dvh - var(--invoice-success-chrome, 112px));
  max-height: calc(100svh - var(--invoice-success-chrome, 112px));
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f5f7fa;
  box-sizing: border-box;
  position: relative;
}

.invoice-mobile-success__header {
  flex: 0 0 auto;
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  background: #fff;
  border-bottom: 1px solid #eeeeee;
}

.invoice-mobile-success__title {
  flex: 1 1 auto;
  margin: 0;
  text-align: center;
  font-size: 16px;
  font-weight: 800;
  color: #263238;
}

.invoice-mobile-success__header-spacer,
.invoice-mobile-success__icon-btn {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
}

.invoice-mobile-success__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
}

.invoice-mobile-success__loading {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
}

.invoice-mobile-success__body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  /* Keep PDF / preview / list / new-invoice clear of bottom nav + Xiaomi chrome */
  padding: 12px 16px calc(36px + env(safe-area-inset-bottom, 0px));
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

.invoice-mobile-success__hero {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 4px 8px 0;
  text-align: center;
}

.invoice-mobile-success__check {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #e8f5e9;
}

.invoice-mobile-success__hero-text {
  margin: 0;
  max-width: 320px;
  font-size: 13px;
  font-weight: 600;
  color: #607d8b;
  line-height: 1.7;
}

.invoice-mobile-success__card {
  padding: 14px 16px;
  border: 1px solid #e0e6ed;
  border-radius: 14px;
  background: #fff;
}

.invoice-mobile-success__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 2px;
  font-size: 13px;
  color: #78909c;
}

.invoice-mobile-success__row strong {
  color: #37474f;
  font-weight: 700;
  text-align: left;
}

.invoice-mobile-success__row.is-emphasis {
  margin-top: 4px;
  padding-top: 14px;
  border-top: 1px solid #e6ebf0;
  color: #546e7a;
}

.invoice-mobile-success__row.is-emphasis strong {
  color: #263238;
  font-size: 14px;
}

.invoice-mobile-success__share {
  flex: 0 0 auto;
  padding: 14px;
  border: 1px solid #e0e6ed;
  border-radius: 16px;
  background: #fff;
}

.invoice-mobile-success__share-title {
  margin: 0 0 12px;
  font-size: 13px;
  font-weight: 800;
  color: #455a64;
  text-align: right;
}

.invoice-mobile-success__share-hub {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 12px;
  align-items: stretch;
}

.invoice-mobile-success__qr-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px;
  border-radius: 16px;
  background: linear-gradient(165deg, #f8fafc 0%, #eef3f8 100%);
  border: 1px solid #e6ebf0;
}

.invoice-mobile-success__qr {
  display: block;
  width: 108px;
  height: 108px;
  border-radius: 12px;
  background: #fff;
  object-fit: contain;
}

.invoice-mobile-success__qr-caption {
  font-size: 10px;
  font-weight: 700;
  color: #90a4ae;
  letter-spacing: 0;
}

.invoice-mobile-success__share-side {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  min-width: 0;
}

.invoice-mobile-success__share-hint {
  margin: 0;
  font-size: 11px;
  font-weight: 600;
  color: #78909c;
  line-height: 1.55;
}

.invoice-mobile-success__copy-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-height: 42px;
  padding: 0 10px;
  border: 1px dashed #cfd8e3;
  border-radius: 11px;
  background: #f7f9fc;
  color: #546e7a;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.invoice-mobile-success__copy-chip-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: right;
}

.invoice-mobile-success__copy-chip:active {
  background: #eef2f7;
}

.invoice-mobile-success__share-twins {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.invoice-mobile-success__twin {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 40px;
  padding: 0 8px;
  border: 1px solid #e3e8ef;
  border-radius: 11px;
  background: #fff;
  color: #546e7a;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.invoice-mobile-success__twin:disabled {
  opacity: 0.55;
  cursor: default;
}

.invoice-mobile-success__twin:active:not(:disabled) {
  background: #f5f7fa;
}

.invoice-mobile-success__twin--accent {
  border: 0;
  background: #5a6b85;
  color: #fff;
}

.invoice-mobile-success__twin--accent:active:not(:disabled) {
  background: #4e5f78;
}

.invoice-mobile-success__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 0 0 auto;
  margin-top: 4px;
  padding-top: 4px;
  padding-bottom: 16px;
}

.invoice-mobile-success__card,
.invoice-mobile-success__share {
  flex-shrink: 0;
}

.invoice-mobile-success__action-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.invoice-mobile-success__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 46px;
  padding: 0 12px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.invoice-mobile-success__btn:disabled {
  opacity: 0.65;
  cursor: default;
}

.invoice-mobile-success__btn--primary {
  border: 0;
  background: #5a6b85;
  color: #fff;
  min-height: 48px;
  font-size: 14px;
}

.invoice-mobile-success__btn--soft {
  border: 1px solid #c5d0e0;
  background: #e8edf5;
  color: #5a6b85;
}

.invoice-mobile-success__btn--outline {
  border: 1px solid #cfd8dc;
  background: #fff;
  color: #546e7a;
}

.invoice-mobile-success__link {
  margin-top: 2px;
  border: 0;
  background: transparent;
  color: #1565c0;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
</style>
